/**
 * La session de partie : le pont entre le moteur de règles et la scène 3D.
 *
 * Module **pur** — ni React ni physique — parce que c'est ici que vivent les deux
 * ou trois raisonnements qu'on ne veut pas voir noyés dans un composant.
 */

import {
  actionsPossibles, actionSuivanteIA, appliquer, creerPartie, deduireDemande,
  lancersAutorises, profilDe, tempoDeReflexion,
  type Action, type Demande, type Face, type Niveau, type Partie,
  type PlaceALaTable, type Tables, type Tireur,
} from "@zinc421/shared";

// La déduction du jet vit dans `shared` : le serveur en a besoin au lot 5, où c'est
// lui qui tire. Réexportée ici pour que la scène n'ait qu'une porte d'entrée.
export { deduireDemande, type Demande };

// ------------------------------------------------------------------ la session

/** Ce qu'on injecte : le hasard, l'IA, et rien d'autre. */
export interface Reglages {
  readonly tables: Tables;
  /** Le tirage des dés. C'est LUI qui décide du résultat, jamais la physique. */
  readonly tireur: Tireur;
  /** Uniforme [0, 1[ pour l'erreur ε des IA et pour leur tempo. */
  readonly alea: () => number;
  /** Graine du geste de lancer. Au lot 5, elle viendra du serveur avec le résultat. */
  readonly graine: () => number;
}

export interface Session {
  /** L'état autoritatif. Il est toujours en avance sur ce que l'écran montre. */
  readonly partie: Partie;
  /** Le jet que la scène doit encore jouer, ou `null` si les dés sont posés. */
  readonly demande: Demande | null;
  /**
   * Les faces réellement posées sur le tapis, dé par dé.
   *
   * Elles suivent la **scène**, pas le moteur : c'est ce que le joueur a sous les
   * yeux. D'où leur persistance entre deux manches — les dés restent physiquement
   * là où ils sont tombés tant qu'on ne les relance pas, alors que `manche.enCours`
   * du moteur, lui, redevient nul dès qu'un jet est posé.
   */
  readonly montre: readonly (Face | null)[];
}

export class JetEnCours extends Error {}

export function ouvrirSession(partie: Partie): Session {
  return { partie, demande: null, montre: [null, null, null] };
}

/**
 * Les trois dés tels que l'écran doit les présenter.
 *
 * Un dé qui roule n'affiche **aucune** valeur : le moteur connaît déjà le résultat,
 * l'écran n'a pas à le vendre avant que les dés s'arrêtent. La garde, elle, vient du
 * moteur sans délai — c'est un geste, pas une animation.
 */
export function desAffiches(
  session: Session,
): ReadonlyArray<{ readonly valeur: Face | null; readonly garde: boolean }> {
  const enVol = new Set(session.demande?.indices ?? []);
  const des = session.partie.manche?.enCours?.des;
  return session.montre.map((valeur, i) => ({
    // Trois cas, et le troisième a coûté cher : le dé roule (aucune valeur), le dé
    // est en main (sa valeur), ou PERSONNE ne l'a en main — le tour est posé, la
    // manche a changé. Les dés restent alors physiquement sur le tapis, mais les
    // montrer comme une main donnait à croire qu'on pouvait s'arrêter dessus.
    valeur: enVol.has(i) || !des ? null : valeur,
    garde: des?.[i]?.garde ?? false,
  }));
}

/**
 * Applique une action et met en attente le jet qu'elle a produit, s'il y en a un.
 *
 * Agir pendant qu'un jet roule est **refusé** plutôt qu'ignoré : le moteur serait
 * alors en avance de deux coups sur la scène, qui montrerait des dés démentis par
 * l'état avant même de s'arrêter.
 */
export function agir(session: Session, action: Action, reglages: Reglages): Session {
  if (session.demande !== null) {
    throw new JetEnCours(`action ${action.type} demandée pendant un jet`);
  }
  const partie = appliquer(session.partie, action, reglages.tireur);
  const aLance = partie.evenements.some((e) => e.type === "LANCE");
  return {
    ...session,
    partie,
    demande: aLance ? deduireDemande(session.partie, partie, reglages.graine()) : null,
  };
}

/** La scène a fini de jouer le jet : l'écran peut rattraper l'état. */
export function jetTermine(session: Session): Session {
  const demande = session.demande;
  if (demande === null) return session;
  const montre = [...session.montre];
  demande.indices.forEach((index, rang) => { montre[index] = demande.faces[rang]!; });
  return { ...session, demande: null, montre };
}

// ------------------------------------------------------------------- les IA

/** Le joueur dont c'est le tour, ou `null` si personne n'a à agir. */
export function aQuiDeJouer(
  partie: Partie,
): { readonly siege: number; readonly nom: string; readonly estIA: boolean } | null {
  const siege = partie.manche?.tourDe;
  if (siege === undefined || siege === null) return null;
  const joueur = partie.joueurs[siege];
  if (!joueur) return null;
  return { siege, nom: joueur.nom, estIA: joueur.estIA };
}

/**
 * L'action que l'IA dont c'est le tour va jouer — **une seule**.
 *
 * Une par une, et non `jouerJusquAuJoueur` d'un bloc : un tour d'IA vaut plusieurs
 * actions, chacune pouvant lancer des dés. Les enchaîner d'un coup ferait apparaître
 * le résultat final sans qu'aucun jet ait été montré, et le joueur ne verrait jamais
 * jouer ses adversaires.
 *
 * Rend `null` quand ce n'est pas à une IA, ou tant qu'un jet roule encore.
 */
export function actionIA(session: Session, reglages: Reglages): Action | null {
  if (session.demande !== null) return null;
  const tour = aQuiDeJouer(session.partie);
  if (!tour?.estIA) return null;
  return actionSuivanteIA(session.partie, reglages.tables, reglages.alea());
}

/**
 * Le temps que met une IA à réagir, en secondes. Zéro pour un humain.
 *
 * Une action sans alternative n'est pas une décision : ouvrir un tour, ou poser
 * quand il ne reste plus de lancer. On y met juste de quoi respirer entre deux
 * animations. C'est `actionsPossibles` qui tranche, et non une heuristique locale :
 * le moteur sait déjà s'il y a un choix.
 */
export const TEMPO_SANS_CHOIX = 0.25;

/**
 * Facteur appliqué au temps de réflexion des adversaires.
 *
 * Le tempo du § 4.2 a été réglé pour qu'une IA ne réponde pas comme un automate.
 * Vu à table, il coûtait trop cher : un tour d'adversaire vaut plusieurs actions,
 * chacune suivie de l'animation de son jet, et une manche à trois joueurs prenait
 * près d'une minute. Olivier l'a dit tout de suite — « ils sont lents ».
 *
 * On garde donc l'hésitation, on la raccourcit. C'est un réglage de rythme, pas une
 * décision de jeu : aucune action n'en dépend.
 */
const RYTHME = 0.45;

/**
 * Le serrement de la décision — l'écart de valeur entre les deux meilleures actions
 * — demanderait une table que l'export ne produit pas. On prend une valeur médiane :
 * l'IA hésite un peu à chaque vraie décision, jamais devant une action forcée. C'est
 * la moitié du bénéfice du § 4.2 pour aucune dette.
 */
const SERREMENT_PAR_DEFAUT = 0.5;

export function tempoAvantAction(session: Session, alea: number): number {
  const tour = aQuiDeJouer(session.partie);
  if (!tour?.estIA) return 0;
  if (actionsPossibles(session.partie).length <= 1) return TEMPO_SANS_CHOIX;
  const niveau = session.partie.joueurs[tour.siege]!.niveauIA as Niveau;
  return RYTHME * tempoDeReflexion(profilDe(niveau), SERREMENT_PAR_DEFAUT, alea);
}

/**
 * Les actions que l'interface peut réellement proposer au joueur humain.
 *
 * Ce n'est pas tout à fait `actionsPossibles` du moteur, et l'écart tient en une
 * ligne : **garder les trois dés, c'est s'arrêter**. Le moteur accepte pourtant un
 * `LANCER` dans cet état — il relancerait zéro dé et consommerait un lancer pour
 * rien. La scène, elle, ne peut pas : `calculerJet([])` lève. Plutôt que d'ajouter
 * une exception dans le moteur, qui est en parité avec le simulateur Python, on
 * retire l'action ici, là où elle est offerte.
 *
 * Vide quand un jet roule, ou quand ce n'est pas au joueur d'agir.
 */
export function actionsOffertes(
  session: Session, monSiege?: number,
): readonly Action["type"][] {
  if (session.demande !== null) return [];
  const tour = aQuiDeJouer(session.partie);
  if (!tour || tour.estIA) return [];
  // En ligne, « c'est à un humain » ne suffit pas : il faut que ce soit à MOI.
  // Sans siège précisé — solo, hot-seat — l'appareil joue pour tous les humains.
  if (monSiege !== undefined && tour.siege !== monSiege) return [];

  const possibles = actionsPossibles(session.partie);
  const des = session.partie.manche?.enCours?.des;
  if (des && des.every((d) => d.garde)) {
    // `GARDER` reste offert, et c'est volontaire : relâcher un dé doit rester
    // possible, sans quoi un clic de trop enfermerait le joueur dans un arrêt qu'il
    // n'a pas choisi.
    return possibles.filter((type) => type !== "LANCER");
  }
  return possibles;
}

/**
 * Vrai quand le joueur n'a plus rien à décider : il ne lui reste qu'à poser.
 *
 * C'est le cas dès qu'il a consommé tous ses lancers. « Je m'arrête » ne décide alors
 * plus de rien — il n'y a pas d'alternative — et le clic ne fait que **bloquer la
 * table** : les autres attendent que quelqu'un valide une main qui est déjà décidée.
 * Olivier l'a signalé comme un blocage, et c'en est un.
 *
 * ⚠️ À ne pas confondre avec « garder les trois dés ». Là, il reste des lancers : le
 * joueur PEUT relâcher un dé et relancer. C'est un choix, et on ne le lui prend pas.
 */
export function poseForcee(session: Session, monSiege?: number): boolean {
  if (session.demande !== null) return false;
  const tour = aQuiDeJouer(session.partie);
  if (!tour || tour.estIA) return false;
  if (monSiege !== undefined && tour.siege !== monSiege) return false;
  const enCours = session.partie.manche?.enCours;
  if (!enCours) return false;
  return enCours.lancers >= lancersAutorises(session.partie);
}

/**
 * L'action qui garde le dé `index`, ou le relâche s'il l'était déjà.
 *
 * L'action `GARDER` du moteur porte la liste **complète** des dés conservés, pas une
 * bascule : elle est idempotente, ce qui la rend rejouable sur le réseau. La bascule,
 * elle, est une notion d'interface — c'est ici qu'elle se traduit.
 *
 * Rend `null` quand garder n'a pas de sens : aucun dé lancé, jet en cours, ou ce
 * n'est pas au joueur d'agir.
 */
export function garderOuRelacher(
  session: Session, index: number, monSiege?: number,
): Action | null {
  if (!actionsOffertes(session, monSiege).includes("GARDER")) return null;
  const des = session.partie.manche?.enCours?.des;
  if (!des) return null;
  const gardes = des.filter((d) => d.garde).map((d) => d.id);
  const voulus = gardes.includes(index as 0 | 1 | 2)
    ? gardes.filter((i) => i !== index)
    : [...gardes, index].sort((a, b) => a - b);
  return { type: "GARDER", des: voulus };
}

// ------------------------------------------------------------ dresser la table

/**
 * Des prénoms de comptoir pour les adversaires virtuels, pas des « Joueur 2 ».
 *
 * Un adversaire nommé se retient et se déteste ; « IA 3 » ne fait ni l'un ni l'autre.
 * Le niveau, lui, est annoncé à l'accueil : le nom n'a pas à le porter.
 */
const HABITUES = ["Marcel", "Jeanine", "Gégé", "Nadine", "Riton"] as const;

/** Nombre de joueurs possibles autour d'un zinc. */
export const TABLE_MIN = 2;
export const TABLE_MAX = 6;

/**
 * Une table : les humains nommés d'abord, des IA pour compléter.
 *
 * On donne **combien on est** et **qui joue** ; le nombre d'adversaires virtuels
 * s'en déduit. C'est l'ordre dans lequel on s'installe autour d'une vraie table, et
 * celui qu'Olivier a demandé — personne ne compte les IA à sa place.
 */
export function dresserTable(
  humains: readonly string[], total: number, niveau: Niveau,
): Partie {
  const places = placesDeLaTable(humains, total, niveau);
  const base = creerPartie(places.map((p) => p.nom));
  return {
    ...base,
    joueurs: base.joueurs.map((j) => ({
      ...j, estIA: places[j.siege]!.estIA, niveauIA: places[j.siege]!.niveauIA,
    })),
  };
}

/**
 * Qui s'installe autour du zinc : les humains nommés d'abord, des IA pour compléter.
 *
 * Rendue à part de la partie, parce qu'une soirée en compte plusieurs — la cave, la
 * recave, la cave des caves. La table, elle, ne change pas d'une cave à l'autre.
 */
export function placesDeLaTable(
  humains: readonly string[], total: number, niveau: Niveau,
): readonly PlaceALaTable[] {
  if (total < TABLE_MIN || total > TABLE_MAX) {
    throw new Error(`une table va de ${TABLE_MIN} à ${TABLE_MAX} joueurs, ${total} demandés`);
  }
  if (humains.length > total) {
    throw new Error(
      `${humains.length} joueurs pour ${total} sièges : il en resterait debout`);
  }

  const nomsHumains = humains.map((brut, i) => nomPropre(brut) || `Joueur ${i + 1}`);
  const virtuels = total - nomsHumains.length;
  const noms = [
    ...nomsHumains,
    ...HABITUES.slice(0, virtuels).map((nom) => nom),
  ];
  // Deux homonymes rendraient la table illisible : « Marcel » a posé quoi, déjà ?
  const vus = new Map<string, number>();
  const uniques = noms.map((nom) => {
    const dejaVu = vus.get(nom) ?? 0;
    vus.set(nom, dejaVu + 1);
    return dejaVu === 0 ? nom : `${nom} ${dejaVu + 1}`;
  });

  return uniques.map((nom, i) => ({
    nom,
    estIA: i >= nomsHumains.length,
    niveauIA: i >= nomsHumains.length ? niveau : 0,
  }));
}

/**
 * Renomme un joueur en pleine partie.
 *
 * À plusieurs sur un même téléphone, personne ne pense à saisir les noms avant de
 * commencer — et trois « Joueur 2 » à table rendent le tableau des scores illisible.
 * Olivier l'a signalé : « on s'appelle tous pareil ».
 *
 * ⚠️ Un nom n'est PAS une donnée de règle : il ne change ni un score, ni un tour, ni
 * une décision d'IA. Le renommer en cours de partie est donc sans conséquence sur
 * l'état de jeu — c'est ce qui rend l'opération sûre, et il faut que ça le reste.
 *
 * Un nom vide est refusé plutôt qu'appliqué : rendre quelqu'un anonyme au milieu
 * d'une manche serait pire que le problème qu'on corrige.
 */
export function renommer(session: Session, siege: number, nom: string): Session {
  const propre = nomPropre(nom);
  if (propre.length === 0) return session;
  return {
    ...session,
    partie: {
      ...session.partie,
      joueurs: session.partie.joueurs.map(
        (j) => (j.siege === siege ? { ...j, nom: propre } : j)),
    },
  };
}

/** Un pseudo lisible : borné, sans caractères de contrôle, sans blancs inutiles. */
export function nomPropre(brut: string): string {
  return brut.replace(/\p{C}/gu, "").trim().slice(0, 16);
}
